// src/components/agents/AgentAvatar.tsx
//
// Avatar redondo do agente (Reforma UX §6.6, AGT-01) — imagem ou fallback de
// iniciais + menu de contexto: ver imagem (fullscreen) · abrir em nova aba ·
// trocar imagem (POST /v1/uploads → PATCH /v1/agents/:slug { avatar_url }).
//
// Degrade graceful (§7.5): backend pré-restart não retorna avatar_url nem
// aceita PATCH avatar — upload mostra toast de erro e mantém as iniciais.

import { useRef, useState } from 'react';
import { api, type AgentListItem } from '@/lib/api';
import { cn } from '@/lib/utils';
import { agentInitials } from './agent-constants';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/DropdownMenu';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { Eye, ExternalLink, Loader2, Upload } from 'lucide-react';

// Wireframe §07: avatar .avround = 52px (md). lg (modal de detalhe) = 64px.
const SIZE_STYLES = {
  md: undefined,
  lg: { width: 64, height: 64, fontSize: '1.25rem' },
} as const;

interface AgentAvatarProps {
  agent: Pick<AgentListItem, 'slug' | 'name' | 'avatar_url' | 'id'>;
  size?: keyof typeof SIZE_STYLES;
  /** Recebe a URL nova do avatar após o PATCH com sucesso. */
  onAvatarUpdated?: (avatarUrl: string) => void;
  onError?: (message: string) => void;
  className?: string;
}

export function AgentAvatar({
  agent,
  size = 'md',
  onAvatarUpdated,
  onError,
  className,
}: AgentAvatarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [imgError, setImgError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);

  const avatarUrl = agent.avatar_url && !imgError ? agent.avatar_url : null;

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      // purpose:'asset' → signed URL de longa duração (~10 anos). O TTL default
      // de 1h matava o avatar: a URL salva expirava e a imagem quebrava.
      // Backend pré-restart ignora o campo (TTL 1h, degrade graceful).
      const uploaded = await api.uploadFile(file, { purpose: 'asset' });
      // PATCH avatar_url — campo novo (§7.5). Backend pré-restart descarta o
      // campo e devolve 422 "Nenhum campo atualizável" → degrade graceful.
      await api.updateAgent(agent.slug || agent.id, {
        avatar_url: uploaded.signed_url,
      });
      setImgError(false);
      onAvatarUpdated?.(uploaded.signed_url);
    } catch (err) {
      onError?.(
        err instanceof Error
          ? `Não foi possível salvar o avatar: ${err.message}`
          : 'Não foi possível salvar o avatar.',
      );
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title={`Avatar de ${agent.name}`}
            aria-label={`Avatar de ${agent.name} — abrir menu`}
            onClick={(e) => e.stopPropagation()}
            // Wireframe .avround (gradiente azul, borda #1d3247, display font).
            className={cn('avround shrink-0 cursor-pointer', className)}
            style={SIZE_STYLES[size]}
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin text-raios-muted" aria-hidden="true" />
            ) : avatarUrl ? (
              <img
                src={avatarUrl}
                alt={`Avatar de ${agent.name}`}
                className="h-full w-full object-cover"
                onError={() => setImgError(true)}
              />
            ) : (
              <span className="select-none">{agentInitials(agent.name || agent.slug)}</span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={4} className="avmenu-pop z-[200]">
          <DropdownMenuItem
            disabled={!avatarUrl}
            onSelect={() => setViewerOpen(true)}
            className="cursor-pointer"
          >
            <Eye className="h-4 w-4 text-raios-muted mr-2" />
            Ver imagem
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!avatarUrl}
            onSelect={() => {
              if (avatarUrl) window.open(avatarUrl, '_blank', 'noopener');
            }}
            className="cursor-pointer"
          >
            <ExternalLink className="h-4 w-4 text-raios-muted mr-2" />
            Abrir em nova aba
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => inputRef.current?.click()}
            className="cursor-pointer"
          >
            <Upload className="h-4 w-4 text-raios-muted mr-2" />
            Trocar imagem
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Lightbox "Ver imagem" */}
      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent size="lg" aria-describedby={undefined}>
          <DialogTitle className="mb-4">Avatar — {agent.name}</DialogTitle>
          {avatarUrl && (
            <img
              src={avatarUrl}
              alt={`Avatar de ${agent.name}`}
              className="max-h-[70vh] w-auto mx-auto rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
